/**
 * Zoom Input Handler Utilities
 *
 * This file contains utility functions for zoom input handling
 * extracted from MyCanvas.js to improve code organization and maintainability.
 */

import { logCanvasHScroll } from "./zoomViewportUtils";

/**
 * Handle zoom input change (only update input value, don't apply zoom yet)
 * @param {Event} e - Input change event
 * @param {Function} setZoomInputValue - Setter for zoom input value
 */
export const createHandleZoomInputChange = (setZoomInputValue) => (e) => {
  // Only allow numeric characters (digits only, no letters or special chars)
  const numericOnly = e.target.value.replace(/[^0-9]/g, "");
  setZoomInputValue(numericOnly);
};

/**
 * Handle zoom input key press (apply zoom when Enter key is pressed)
 * @param {Event} e - Key press event
 * @param {Function} handleManualZoom - Function to handle manual zoom
 */
export const createHandleZoomInputKeyPress = (handleManualZoom) => (e, zoomLevel) => {
  // Apply zoom when Enter key is pressed
  if (e.key === "Enter") {
    handleManualZoom(e, zoomLevel);
    e.target.blur(); // Remove focus from input
  }
};

/**
 * Handle manual zoom from input field
 * @param {Event} e - Input event
 * @param {number} zoomLevel - Current zoom level
 * @param {Function} setZoomLevel - Setter for zoom level
 * @param {Function} setZoomInputValue - Setter for zoom input value
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} scrollAreaRef - Reference to scroll area
 * @param {Object} scaledContentRef - Reference to scaled canvas wrapper (unused; API stability)
 * @param {Object} stageRef - Main stage ref (unused; API stability)
 * @param {{ current: { left: number; maxL: number } | null } | null} preZoomHScrollRef - Snapshot target for horizontal scroll ratio preservation
 */
export const createHandleManualZoom =
  (
    setZoomLevel,
    setZoomInputValue,
    saveToHistory,
    scrollAreaRef,
    scaledContentRef,
    stageRef,
    preZoomHScrollRef,
  ) =>
    (e, zoomLevel) => {
      const currentZoom = zoomLevel || 1;
      try {
        const value = parseFloat(e.target.value) / 100;
        if (!isNaN(value) && value > 0) {
          const currentScale = currentZoom;

          // Clamp between 0.1 and 5.0
          const clampedValue = Math.max(0.1, Math.min(value, 5));

          const zoomRatio = clampedValue / currentScale;

          setZoomLevel(clampedValue);
          setZoomInputValue(Math.round(clampedValue * 100)); // Update input value to match applied zoom

          if (zoomRatio !== 1 && preZoomHScrollRef) {
            logCanvasHScroll("handleManualZoom", {
              currentScale,
              clampedValue,
              zoomRatio,
            });
            if (clampedValue !== currentScale) {
              preZoomHScrollRef.current = { mode: "center" };
            }
          }

          saveToHistory();
        } else {
          // If invalid input, reset to current zoom level
          setZoomInputValue(Math.round(currentZoom * 100));
        }
      } catch (error) {
        // Reset to current zoom level on error
        setZoomInputValue(Math.round(currentZoom * 100));
      }
    };
